import { useState } from "react";
import { Box, Button, Link, Paper, Stack, Typography } from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import CopyButton from "@/components/CopyButton";
import { groupSecret, MONO } from "../helpers";
import type { MfaSetup } from "../types";
import CodeField from "./CodeField";
import { FormError } from "./State";

/**
 * Enrolling an authenticator: the key to add, then a code to prove it works.
 *
 * The QR code is drawn in the browser from the otpauth link, so the secret is
 * never sent anywhere to be rendered. The key stays below it for apps that
 * cannot scan, with the link for phones, where tapping it opens the app.
 */
export default function MfaEnrolment({
  setup, busy, error, onActivate, submitLabel = "Turn on two-factor",
}: {
  setup: MfaSetup;
  busy: boolean;
  error: unknown;
  onActivate: (code: string) => void;
  submitLabel?: string;
}) {
  const [code, setCode] = useState("");

  return (
    <Stack
      component="form"
      spacing={2.5}
      onSubmit={(event) => {
        event.preventDefault();
        if (code.length === 6) onActivate(code);
      }}
    >
      <Box>
        <Typography variant="subtitle2" fontWeight={600}>
          1. Scan this code with your authenticator app
        </Typography>
        <Typography variant="body2" color="text.secondary">
          In Google Authenticator, Microsoft Authenticator, 1Password or similar,
          choose to add an account and scan the QR code.
        </Typography>
        <Box
          sx={{
            mt: 1.5, p: 1.5, width: "fit-content", bgcolor: "common.white",
            border: 1, borderColor: "divider", borderRadius: 1, lineHeight: 0,
          }}
        >
          <QRCodeSVG value={setup.otpauth_uri} size={176} level="M" title="Two-factor setup QR code" />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Can’t scan? Choose to enter a setup key instead, and type or paste this one (time-based).
        </Typography>
        <Paper
          variant="outlined"
          sx={{ mt: 1, px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1 }}
        >
          <Typography
            sx={{ fontFamily: MONO, fontSize: 17, letterSpacing: "0.06em", flex: 1, wordBreak: "break-all" }}
          >
            {groupSecret(setup.secret)}
          </Typography>
          <CopyButton value={setup.secret} label="Copy key" />
        </Paper>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            On this phone? <Link href={setup.otpauth_uri}>Open in your authenticator app</Link>
            {" "}or copy the setup link
          </Typography>
          <CopyButton value={setup.otpauth_uri} label="Copy setup link" />
        </Stack>
      </Box>

      <Box>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
          2. Enter the code it shows
        </Typography>
        <CodeField value={code} onChange={setCode} disabled={busy} autoFocus={false} />
      </Box>

      <FormError error={error} />
      <Button type="submit" variant="contained" disabled={busy || code.length !== 6}>
        {busy ? "Checking…" : submitLabel}
      </Button>
    </Stack>
  );
}
